import { routePrefixes } from "../../next.config/routing/config";
import type { CountryType } from "../@types/generic";
import type { PageParams, PageTypeObject } from "../app/types";
import { isDachProduct } from "./isDachProduct";

const defaultPageType: PageTypeObject = {
    isAnyForecastPage: false,
    isForecastTodayPage: false,
    isForecastTomorrowPage: false,
    isForecastSinglePage: false,
    isForecastMultiplePage: false,
    isForecastWeekendPage: false,
    isHomePage: false,
    isPlainPage: false,
    isAnySkiAreasPage: false,
    isAnySkiAreaDetailPage: false,
    isSkiAreasPage: false,
    isSkiAreaDetailPage: false,
    isSkiAreaWeatherAndSnowPage: false,
    isSkiAreaSlopesAndLiftsPage: false,
    isAnyHealthPollenDictionaryPage: false,
    isHealthPollenDictionaryPage: false,
    isHealthPollenDictionaryByCharacterPage: false,
    isHealthPollenDictionaryEntryPage: false,
    isLivecamLandingPage: false,
    isLivecamDetailPage: false,
    isLivecamInformationPage: false,
    isAnyLivecamPage: false,
    isImprintPage: false,
    isCompliancePage: false,
    isPrivacyPage: false,
};

export const pageTypes = {
    plain: { ...defaultPageType, isPlainPage: true },
    home: { ...defaultPageType, isHomePage: true },
    forecastToday: {
        ...defaultPageType,
        isAnyForecastPage: true,
        isForecastTodayPage: true,
    },
    forecastTomorrow: {
        ...defaultPageType,
        isAnyForecastPage: true,
        isForecastSinglePage: true,
        isForecastTomorrowPage: true,
    },
    forecastSingle: {
        ...defaultPageType,
        isAnyForecastPage: true,
        isForecastSinglePage: true,
    },
    forecastMultiple: {
        ...defaultPageType,
        isAnyForecastPage: true,
        isForecastMultiplePage: true,
    },
    forecastWeekend: {
        ...defaultPageType,
        isAnyForecastPage: true,
        isForecastMultiplePage: true,
        isForecastWeekendPage: true,
    },
    skiAreas: {
        ...defaultPageType,
        isAnySkiAreasPage: true,
        isSkiAreasPage: true,
    },
    skiAreaDetail: {
        ...defaultPageType,
        isAnySkiAreasPage: true,
        isAnySkiAreaDetailPage: true,
        isSkiAreaDetailPage: true,
    },
    skiAreaWeatherAndSnow: {
        ...defaultPageType,
        isAnySkiAreasPage: true,
        isAnySkiAreaDetailPage: true,
        isSkiAreaWeatherAndSnowPage: true,
    },
    skiAreaSlopesAndLifts: {
        ...defaultPageType,
        isAnySkiAreasPage: true,
        isAnySkiAreaDetailPage: true,
        isSkiAreaSlopesAndLiftsPage: true,
    },
    healthPollenDictionary: {
        ...defaultPageType,
        isAnyHealthPollenDictionaryPage: true,
        isHealthPollenDictionaryPage: true,
    },
    healthPollenDictionaryByCharacter: {
        ...defaultPageType,
        isAnyHealthPollenDictionaryPage: true,
        isHealthPollenDictionaryByCharacterPage: true,
    },
    healthPollenDictionaryEntry: {
        ...defaultPageType,
        isAnyHealthPollenDictionaryPage: true,
        isHealthPollenDictionaryEntryPage: true,
    },
    livecamLanding: {
        ...defaultPageType,
        isAnyLivecamPage: true,
        isLivecamLandingPage: true,
    },
    livecamDetail: {
        ...defaultPageType,
        isAnyLivecamPage: true,
        isLivecamDetailPage: true,
    },
    livecamInformation: {
        ...defaultPageType,
        isAnyLivecamPage: true,
        isLivecamInformationPage: true,
    },
    imprint: { ...defaultPageType, isImprintPage: true },
    compliance: { ...defaultPageType, isCompliancePage: true },
    privacy: { ...defaultPageType, isPrivacyPage: true },
} as const satisfies Record<string, PageTypeObject>;

export const extractPageParamsFromPathname = (
    pathname: string,
    country: CountryType
): PageParams => {
    if (!isDachProduct(country)) {
        return extractPl(pathname);
    }

    return extractDACH(pathname);
};

const extractDACH = (pathname: string): PageParams => {
    /**
     * FORECAST
     */
    let params = pathname.match(
        /\/wetter_aktuell\/wettervorhersage\/(?<daysMultiple>3|7|16)_tagesvorhersage\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,}).html/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastMultiple,
        };
    }

    params = pathname.match(
        /\/wetter_aktuell\/wettervorhersage\/(?<dayStringMultiple>wochenend)_vorhersage\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,}).html/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastWeekend,
        };
    }

    params = pathname.match(
        /\/wetter_aktuell\/wettervorhersage\/(?<dayStringSingle>morgen)\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,}).html/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastTomorrow,
        };
    }

    params = pathname.match(
        /\/wetter_aktuell\/wettervorhersage\/in-(?<daysSingle>[2-6])-tagen\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,}).html/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastSingle,
        };
    }

    params = pathname.match(
        /\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,}).html/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastToday,
        };
    }

    /**
     * HD LIVE WEBCAMS
     */
    params = pathname.match(
        /hd-live-webcams\/(?<seoString>[a-zA-Z0-9-/]+)\/(?<id>[a-z0-9]+)/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.livecamDetail,
        };
    }

    /**
     * SKIAREAS
     */
    params = pathname.match(
        /\/skigebiete\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/pisten-lifte\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaSlopesAndLifts,
        };
    }
    params = pathname.match(
        /\/skigebiete\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/wetter-schnee\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaWeatherAndSnow,
        };
    }
    params = pathname.match(
        /\/skigebiete\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaDetail,
        };
    }

    return { pageType: extractStaticPageType(pathname, "dach") };
};

const extractPl = (pathname: string): PageParams => {
    /**
     * FORECAST
     */
    let params = pathname.match(
        /\/pogoda24\/prognoza-(?<daysMultiple>7|16)-dni\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,})/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastMultiple,
        };
    }

    params = pathname.match(
        /\/pogoda24\/prognoza-(?<dayStringMultiple>weekendowa)\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,})/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastWeekend,
        };
    }

    // today
    params = pathname.match(
        /\/pogoda24\/prognoza-dzisiaj\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,})/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastToday,
        };
    }

    // tomorrow
    params = pathname.match(
        /\/pogoda24\/prognoza-(?<dayStringSingle>na-jutro)\/(?<seoString>.*)\/(?<code>[A-Za-z0-9]{2,})/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.forecastTomorrow,
        };
    }

    /**
     * SKIAREAS
     */
    params = pathname.match(
        /\/pogoda24\/osrodki-narciarskie\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/pogoda-snieg\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaWeatherAndSnow,
        };
    }

    params = pathname.match(
        /\/pogoda24\/osrodki-narciarskie\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/stoki-wyciagi\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaSlopesAndLifts,
        };
    }

    params = pathname.match(
        /\/pogoda24\/osrodki-narciarskie\/(?<countryName>[a-zA-Z-]+)\/(?<name>[a-zA-Z0-9-]+)-(?<id>[0-9]+)\/?$/
    );
    if (params) {
        return {
            ...normalizeParams(params.groups),
            pageType: pageTypes.skiAreaDetail,
        };
    }

    return { pageType: extractStaticPageType(pathname, "pl") };
};

const extractStaticPageType = (
    pathname: string,
    countryType: "dach" | "pl"
): PageTypeObject => {
    if (pathname === "" || pathname === "/__DEFAULT__/") {
        return pageTypes.plain;
    }

    if (pathname === `/${routePrefixes.pl}/`) {
        return pageTypes.home;
    }

    if (countryType === "dach" && pathname === "/") {
        return pageTypes.home;
    }

    if (pathname === "/hd-live-webcams/") {
        return pageTypes.livecamLanding;
    }

    if (pathname === "/hd-live-webcams/information/") {
        return pageTypes.livecamInformation;
    }

    if (
        pathname === "/skigebiete/" ||
        pathname === `/${routePrefixes.pl}/osrodki-narciarskie/`
    ) {
        return pageTypes.skiAreas;
    }

    if (/\/gesundheit\/pollenflug\/pollenlexikon\/.+\/.+/i.test(pathname)) {
        return pageTypes.healthPollenDictionaryEntry;
    }

    if (/\/gesundheit\/pollenflug\/pollenlexikon\/.+/i.test(pathname)) {
        return pageTypes.healthPollenDictionaryByCharacter;
    }

    if (/\/gesundheit\/pollenflug\/pollenlexikon\/?$/i.test(pathname)) {
        return pageTypes.healthPollenDictionary;
    }

    if (/\/impressum\/?$/i.test(pathname)) {
        return pageTypes.imprint;
    }

    if (/\/compliance\/?$/i.test(pathname)) {
        return pageTypes.compliance;
    }

    if (
        /\/datenschutz\/?$/i.test(pathname) ||
        /\/privacy\/?$/i.test(pathname)
    ) {
        return pageTypes.privacy;
    }

    return defaultPageType;
};

const normalizeParams = (
    params: { [key: string]: string } | undefined
): Record<string, string> => {
    if (!params) {
        return {};
    }

    const newParams = { ...params };
    if ("code" in params) {
        newParams.code = params.code.toUpperCase();
    }
    if ("seoString" in params) {
        newParams.seoString = params.seoString.toLowerCase();
    }
    if ("dayStringMultiple" in params) {
        newParams.dayStringMultiple = params.dayStringMultiple.toLowerCase();
    }
    if ("dayStringSingle" in params) {
        newParams.dayStringSingle = params.dayStringSingle.toLowerCase();
    }
    if ("countryName" in params) {
        newParams.countryName = params.countryName.toLowerCase();
    }
    if ("name" in params) {
        newParams.name = params.name.toLowerCase();
    }

    return newParams;
};
